<template>
  <aside
    v-if="renderContainer"
    :class="['adsterra-ad', `adsterra-ad--${resolvedKind}`, customClass, { 'adsterra-ad--confirmed-fill': confirmedFill }]"
    :aria-label="`${placementLabel} advertisement`"
    :data-ad-placement="placement"
    :data-ad-status="adStatus"
    :data-ad-unit="unit ? unit.flag : ''"
  >
    <div class="adsterra-ad__label">Advertisement</div>
    <div ref="slot" class="adsterra-ad__slot"></div>
  </aside>
</template>

<script>
import { matterlaneAutomationDetected } from '../../analytics/matterlaneAutomation';
const AD_UNITS = {
  horizontalDesktop: {
    key: 'd50294ccf556a8a94b6388bb8897c95c',
    flag: '728x90',
    width: 728,
    height: 90,
    src: 'https://www.highperformanceformat.com/d50294ccf556a8a94b6388bb8897c95c/invoke.js'
  },
  rectangleMobile: {
    key: '337810d19f1c23c18bea15bbafa9db11',
    flag: '300x250',
    width: 300,
    height: 250,
    src: 'https://www.highperformanceformat.com/337810d19f1c23c18bea15bbafa9db11/invoke.js'
  },
  rail: {
    key: 'b218574784ca49e8d80eb29fd74cccc9',
    flag: '160x600',
    width: 160,
    height: 600,
    src: 'https://www.highperformanceformat.com/b218574784ca49e8d80eb29fd74cccc9/invoke.js'
  }
};
let runtimeConfigPromise = null;

function loadRuntimeConfig() {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = fetch('/adsterra-config.json', { cache: 'no-store' })
      .then(response => (response.ok ? response.json() : null))
      .catch(() => null);
  }
  return runtimeConfigPromise;
}

function recordDiagnostic(placement, unit, status, detail = '') {
  if (typeof window === 'undefined') {
    return;
  }
  if (!Array.isArray(window.__VIBERARY_AD_DIAGNOSTICS__)) {
    window.__VIBERARY_AD_DIAGNOSTICS__ = [];
  }
  window.__VIBERARY_AD_DIAGNOSTICS__.push({
    placement,
    unit,
    status,
    detail,
    timestamp: new Date().toISOString()
  });
  if (window.__VIBERARY_AD_DIAGNOSTICS__.length > 100) {
    window.__VIBERARY_AD_DIAGNOSTICS__.splice(0, window.__VIBERARY_AD_DIAGNOSTICS__.length - 100);
  }
}

export default {
  name: 'AdsterraBanner',
  props: {
    kind: {
      type: String,
      default: 'horizontal'
    },
    placement: {
      type: String,
      required: true
    },
    customClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loaded: false,
      unsafeRoute: false,
      resolvedKind: this.kind,
      unit: null,
      runtimeConfig: null,
      configReady: false,
      adDisplayReady: false,
      confirmedFill: false,
      noFill: false,
      adStatus: 'waiting_for_config',
      probeToken: 0,
      noFillTimer: null,
      sandboxFrame: null
    };
  },
  computed: {
    renderContainer() {
      return this.adsEnabled && !this.unsafeRoute && !!this.unit && this.unitEnabled(this.unit) && this.adDisplayReady && !this.noFill;
    },
    adsEnabled() {
      if (!this.configReady || !this.runtimeConfig) {
        return false;
      }
      return this.runtimeConfig.enabled === true;
    },
    placementLabel() {
      return this.placement.replace(/[-_]/g, ' ');
    }
  },
  mounted() {
    this.onResize = this.handleResize.bind(this);
    this.onAdMessage = this.handleAdMessage.bind(this);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('message', this.onAdMessage);
    this.configureUnit();
    this.updateRouteSafety();
    // MES-AUT-SUP-VIB-1 (SDR-2026-005). Evaluated per call, never cached at
    // module load. Only an exact, positively established automation marker
    // reaches this branch; every other state - absent, malformed, partial,
    // unknown, or a parsing error - falls through to the unchanged loader
    // below, so placement, timing, no-fill handling and retry behaviour are
    // untouched for real visitors. Reuses the existing runtime_disabled state
    // rather than introducing a new rendering path.
    if (matterlaneAutomationDetected()) {
      this.setAdStatus('runtime_disabled');
      return;
    }
    loadRuntimeConfig().then(config => {
      this.runtimeConfig = config;
      this.configReady = true;
      if (!config) {
        this.setAdStatus('config_error');
      } else if (config.enabled !== true) {
        this.setAdStatus('runtime_disabled');
      } else {
        this.setAdStatus('configured');
      }
      this.configureUnit();
      this.prepareAd();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('message', this.onAdMessage);
    if (this.noFillTimer) {
      window.clearTimeout(this.noFillTimer);
    }
  },
  methods: {
    configureUnit() {
      if (this.kind === 'rail') {
        if (window.innerWidth < 1200) {
          this.unit = null;
          return;
        }
        this.resolvedKind = 'rail';
        this.unit = AD_UNITS.rail;
        return;
      }

      if (window.innerWidth < 768) {
        this.resolvedKind = 'rectangle-mobile';
        this.unit = AD_UNITS.rectangleMobile;
      } else {
        this.resolvedKind = 'horizontal-desktop';
        this.unit = AD_UNITS.horizontalDesktop;
      }
    },
    handleResize() {
      const previousKey = this.unit ? this.unit.key : '';
      this.configureUnit();
      const nextKey = this.unit ? this.unit.key : '';
      if (previousKey !== nextKey) {
        this.prepareAd();
      }
    },
    updateRouteSafety() {
      this.unsafeRoute = false;
    },
    clearAd() {
      this.loaded = false;
      this.sandboxFrame = null;
      if (this.$refs.slot) {
        this.$refs.slot.innerHTML = '';
      }
    },
    resetAdState() {
      this.probeToken += 1;
      this.adDisplayReady = false;
      this.confirmedFill = false;
      this.noFill = false;
      this.adStatus = 'reset';
      if (this.noFillTimer) {
        window.clearTimeout(this.noFillTimer);
        this.noFillTimer = null;
      }
      this.clearAd();
    },
    unitEnabled(unit) {
      if (!unit) {
        return false;
      }
      if (!this.runtimeConfig || !this.runtimeConfig.units) {
        return false;
      }
      return this.runtimeConfig.units[unit.flag] === true;
    },
    prepareAd() {
      this.resetAdState();
      if (!this.adsEnabled || this.unsafeRoute || !this.unit || !this.unitEnabled(this.unit)) {
        if (this.unsafeRoute) {
          this.setAdStatus('route_disabled');
        } else if (!this.adsEnabled) {
          this.setAdStatus(this.configReady ? 'runtime_disabled' : 'waiting_for_config');
        } else if (!this.unit) {
          this.setAdStatus('viewport_disabled');
        } else {
          this.setAdStatus('unit_disabled');
        }
        return;
      }

      this.adDisplayReady = true;
      this.setAdStatus('slot_enabled');
      this.$nextTick(this.injectAd);
    },
    injectAd() {
      if (this.loaded || !this.renderContainer || !this.unit || !this.$refs.slot) {
        return;
      }

      const sandbox = document.createElement('iframe');
      sandbox.className = 'adsterra-ad__sandbox';
      sandbox.title = `${this.placementLabel} advertisement`;
      sandbox.width = String(this.unit.width);
      sandbox.height = String(this.unit.height);
      sandbox.setAttribute('sandbox', 'allow-scripts allow-same-origin');
      sandbox.setAttribute('scrolling', 'no');
      sandbox.setAttribute('loading', 'lazy');
      sandbox.dataset.viberaryAdPlacement = this.placement;
      const messagePayload = JSON.stringify({
        type: 'viberary-ad-status',
        placement: this.placement,
        unit: this.unit.flag
      });
      const adDocument = [
        '<!doctype html><html><head><meta charset="utf-8">',
        `<style>html,body{margin:0;padding:0;width:${this.unit.width}px;height:${this.unit.height}px;overflow:hidden;background:transparent;}</style>`,
        '</head><body>',
        '<scr' + 'ipt>',
        `(function(){var base=${messagePayload};var send=function(status){parent.postMessage(Object.assign({},base,{status:status}),'*');};`,
        `var visible=function(){return Array.prototype.some.call(document.querySelectorAll('iframe,img,video,canvas,object,embed,a'),function(el){var r=el.getBoundingClientRect();var s=getComputedStyle(el);return s.display!=='none'&&s.visibility!=='hidden'&&Number(s.opacity)!==0&&Math.max(r.width,el.offsetWidth||0,Number(el.getAttribute('width'))||0)>=50&&Math.max(r.height,el.offsetHeight||0,Number(el.getAttribute('height'))||0)>=40;});};`,
        `window.__viberaryAdStatus=send;window.__viberaryAdCreativeCheck=function(){if(visible()){send('creative_detected');}};`,
        `new MutationObserver(window.__viberaryAdCreativeCheck).observe(document.body,{childList:true,subtree:true,attributes:true});send('loader_requested');})();`,
        '</scr' + 'ipt>',
        '<scr' + 'ipt>',
        `atOptions = {'key':'${this.unit.key}','format':'iframe','height':${this.unit.height},'width':${this.unit.width},'params':{}};`,
        '</scr' + 'ipt>',
        `<scr` + `ipt src="${this.unit.src}" onload="window.__viberaryAdStatus('loader_loaded');window.__viberaryAdCreativeCheck();" onerror="window.__viberaryAdStatus('loader_error');"></scr` + `ipt>`,
        '</body></html>',
      ].join('');
      sandbox.srcdoc = adDocument;

      this.clearAd();
      this.$refs.slot.appendChild(sandbox);
      this.sandboxFrame = sandbox;
      this.loaded = true;
      this.setAdStatus('loader_requested');
      this.scheduleNoFillCheck(sandbox, this.probeToken);
    },
    setAdStatus(status, detail = '') {
      this.adStatus = status;
      recordDiagnostic(this.placement, this.unit ? this.unit.flag : '', status, detail);
    },
    handleAdMessage(event) {
      const payload = event && event.data;
      if (!payload || payload.type !== 'viberary-ad-status' || event.source !== (this.sandboxFrame && this.sandboxFrame.contentWindow)) {
        return;
      }
      if (payload.placement !== this.placement || payload.unit !== (this.unit && this.unit.flag)) {
        return;
      }
      if (payload.status === 'creative_detected') {
        this.confirmedFill = true;
        this.setAdStatus('filled');
        if (this.noFillTimer) {
          window.clearTimeout(this.noFillTimer);
          this.noFillTimer = null;
        }
        return;
      }
      if (payload.status === 'loader_loaded' || payload.status === 'loader_error' || payload.status === 'loader_requested') {
        this.setAdStatus(payload.status);
      }
    },
    scheduleNoFillCheck(frame, token) {
      const checkDelays = [4000, 10000, 20000, 30000];
      let checkIndex = 0;
      const runCheck = () => {
        if (token !== this.probeToken || !frame) {
          return;
        }

        let hasVisibleCreative = false;
        try {
          const body = frame.contentDocument && frame.contentDocument.body;
          hasVisibleCreative = this.hasVisibleCreative(body);
        } catch (error) {
          hasVisibleCreative = true;
        }

        if (hasVisibleCreative) {
          this.confirmedFill = true;
          this.setAdStatus('filled');
          return;
        }

        checkIndex += 1;
        if (checkIndex < checkDelays.length) {
          this.noFillTimer = window.setTimeout(runCheck, checkDelays[checkIndex] - checkDelays[checkIndex - 1]);
          return;
        }

        this.noFill = true;
        this.adDisplayReady = false;
        this.setAdStatus('no_fill');
        this.clearAd();
        this.$forceUpdate();
      };
      this.noFillTimer = window.setTimeout(runCheck, checkDelays[0]);
    },
    hasVisibleCreative(body) {
      if (!body) {
        return false;
      }

      const candidates = Array.from(body.querySelectorAll('iframe,img,video,canvas,object,embed,a'));
      return candidates.some(element => {
        const style = element.ownerDocument.defaultView.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
          return false;
        }

        const rect = element.getBoundingClientRect();
        const width = Math.max(rect.width, element.offsetWidth || 0, Number(element.getAttribute('width')) || 0);
        const height = Math.max(rect.height, element.offsetHeight || 0, Number(element.getAttribute('height')) || 0);
        if (width < 20 || height < 20) {
          return false;
        }

        const tag = element.tagName.toLowerCase();
        if (tag === 'iframe') {
          // Adsterra can render a real creative in a document-written iframe
          // whose src remains empty/about:blank. Geometry is the reliable
          // signal here; zero-size/no-insert no-fill still collapses.
          return width >= 50 && height >= 40;
        }

        if (tag === 'a' && !element.getAttribute('href')) {
          return false;
        }

        return true;
      });
    }
  },
  watch: {
    '$route.fullPath'() {
      this.updateRouteSafety();
      if (this.unsafeRoute) {
        this.resetAdState();
        return;
      }
      this.configureUnit();
      this.prepareAd();
    }
  }
};
</script>

<style scoped>
.adsterra-ad {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  margin: 22px auto;
  max-width: 100%;
  text-align: center;
}
.adsterra-ad:not(.adsterra-ad--confirmed-fill) {
  opacity: 0;
  pointer-events: none;
}
.adsterra-ad:not(.adsterra-ad--confirmed-fill) .adsterra-ad__label {
  display: none;
}
.adsterra-ad__label {
  color: #777;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  line-height: 1;
  text-transform: uppercase;
}
.adsterra-ad__slot {
  align-items: center;
  display: flex;
  justify-content: center;
  max-width: 100%;
  overflow: hidden;
}
.adsterra-ad--horizontal-desktop .adsterra-ad__slot {
  min-height: 90px;
  width: 728px;
}
.adsterra-ad--rectangle-mobile .adsterra-ad__slot {
  min-height: 250px;
  width: 300px;
}
.adsterra-ad--rail {
  margin-top: 0;
  position: sticky;
  top: 96px;
}
.adsterra-ad--rail .adsterra-ad__slot {
  min-height: 600px;
  width: 160px;
}
.adsterra-ad__sandbox {
  border: 0;
  display: block;
  max-width: 100%;
  overflow: hidden;
}
@media (max-width: 767px) {
  .adsterra-ad {
    margin: 16px auto;
  }
}
</style>
